import logger from './logger';

/**
 * Log a custom Firebase Analytics event.
 * @param {string} name - Event name
 * @param {object} params - Optional event parameters
 */
export const logEvent = (name, params = {}) => {
    if (window.firebase) {
        logger.debug('[Analytics] logEvent:', name, params);
        window.firebase.analytics().logEvent(name, params);
    } else {
        logger.warn('[Analytics] Firebase not loaded, skipping logEvent:', name);
    }
};

/**
 * Log a screen view event (screen_view).
 * Used for direct/manual screen tracking where the screen name is already known.
 * Matches Android Activity tracking pattern.
 * @param {string} screenName - Human-readable screen name (e.g. 'KidAreaActivity')
 * @param {string} screenClass - Screen class (match Android Activity name)
 */
export const logScreenView = (screenName, screenClass) => {
    logEvent('screen_view', {
        firebase_screen: screenName,
        firebase_screen_class: screenClass,
    });
};

export const getScreenNameFromPath = (path, isAuthenticated = false) => {
    if (!path) return 'MainActivity';
    
    let cleanPath = path.toLowerCase();

    // Strip staging basepath if it exists so mapping works on staging URL structures
    const stagingBasepath = '/fr/sfr_fr_kids_webapp_staging/';
    if (cleanPath.indexOf(stagingBasepath) === 0) {
        cleanPath = cleanPath.substring(stagingBasepath.length - 1); // Keep leading '/'
    }

    if (cleanPath === '/' || cleanPath === '/logout' || cleanPath === '/login') {
        return isAuthenticated ? 'ParentAreaActivity' : 'WelcomeActivity';
    }

    if (cleanPath.includes('pincreation')) return 'AuthActivity';
    if (cleanPath.includes('pinverification')) return 'LoginActivity';

    // ParentProfile vs ParentSettings
    if (cleanPath.includes('settings')) {
        return 'ParentProfileActivity';
    }

    if (cleanPath.includes('downloads') ||
        cleanPath.includes('subinfo') ||
        cleanPath.includes('changepin') ||
        cleanPath.includes('selectlanguage') ||
        cleanPath.includes('tutorial') ||
        cleanPath.includes('contact') ||
        cleanPath.includes('help') ||
        cleanPath.includes('info') ||
        cleanPath.includes('sfrapps')) {
        return 'ParentSettingsActivity';
    }

    if (cleanPath.includes('createkidprofile') || cleanPath.includes('kidprofile')) {
        return 'KidProfileActivity';
    }

    if (cleanPath.includes('contentusage') || cleanPath.includes('contentaccess') || cleanPath === '/parentzone' || cleanPath === '/kids') {
        return 'ParentAreaActivity';
    }

    if (cleanPath.includes('stories') ||
        cleanPath.includes('features') ||
        cleanPath.includes('favorites') ||
        cleanPath.includes('jeux') ||
        cleanPath.includes('comptines') ||
        cleanPath.includes('videos')) {
        return 'KidAreaActivity';
    }

    return 'MainActivity';
};

/**
 * Log a page_view event derived from the current React Router path.
 * This is what the Firebase Web SDK actually reads to populate the
 * "Pages and screens" report (unlike screen_view which is Android/iOS native only).
 * Updates document.title to the mapped Activity name for consistency.
 * @param {string} path - The current route path (e.g. window.location.pathname)
 * @param {boolean} isAuthenticated - Whether the user is authenticated (affects root '/' mapping)
 */
export const logScreenViewFromPath = (path, isAuthenticated = false) => {
    const screenName = getScreenNameFromPath(path, isAuthenticated);

    // Update document.title so Firebase auto-collection also picks it up
    document.title = screenName;

    // page_view is what Firebase Web SDK uses for the "Pages and screens" report
    logEvent('page_view', {
        page_title: screenName,
        page_location: window.location.href,
        page_path: path,
    });
};
